import { ref } from 'vue';
import { t } from '@/utils/i18n-helper';
import invitacionesService from '@/services/perfil/invitaciones';
import terminosService from '@/services/perfil/terminos';
import { toError } from '@/services/perfil/_normalize';

/**
 * useStepperResumen — Estado y operaciones del Paso 4 (resumen y confirmación).
 *
 * Responsabilidades:
 *  - Términos y condiciones.
 *  - Procesamiento de la compra sin gastos de gestión.
 *  - Almacenamiento del resultado para redirección.
 *
 * @param {import('vue').Ref<Object|null>} evento     Ref del evento activo
 * @param {() => string|number}             getAbonadoId Función que retorna el ID del abonado
 * @param {import('vue').Ref<string|null>}  sessionId  Ref del sessionId activo
 */
export function useStepperResumen(evento, getAbonadoId, sessionId) {

    // ─── State ────────────────────────────────────────────────────────────
    const terminosAceptados = ref({
        privacidad: false,
        condiciones: false,
        comunicaciones: false,
    });

    const loadingProcesar = ref(false);
    const errorProcesar    = ref(null);

    /**
     * Resultado final de procesarCompraSinGG.
     * Estructura: { session_id, n_pedido }
     */
    const resultadoCompra = ref(null);

    // ─── Procesamiento ────────────────────────────────────────────────────

    /**
     * Ejecuta la compra sin gastos de gestión (amount === 0).
     * Si tiene éxito, guarda el resultado para que la vista padre redirija.
     *
     * @returns {Promise<boolean>} `true` si la compra se procesó correctamente
     */
    const procesarCompra = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId || !evento.value || !sessionId.value) return false;

        loadingProcesar.value = true;
        errorProcesar.value = null;

        try {
            // ── GDPR: guardar aceptación de términos ANTES de procesar la compra ──
            await terminosService.guardarTerminos({
                privacidad:     terminosAceptados.value.privacidad,
                condiciones:    terminosAceptados.value.condiciones,
                comunicaciones: terminosAceptados.value.comunicaciones,
                session_id:     sessionId.value,
            });

            const result = await invitacionesService.procesarCompraSinGG(
                abonadoId,
                evento.value.evento_id,
                evento.value.sesion_id,
                sessionId.value,
            );
            resultadoCompra.value = result;
            return true;
        } catch (err) {
            const normalized = toError(err);
            errorProcesar.value = normalized.message ??
                t('perfil.invitaciones.stepper.error_procesar', {}, 'Error al procesar las invitaciones. Por favor, inténtalo de nuevo.');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperResumen] Error al procesar compra:', err);
            }
            return false;
        } finally {
            loadingProcesar.value = false;
        }
    };

    // ─── Reset ────────────────────────────────────────────────────────────
    const reset = () => {
        terminosAceptados.value = {
            privacidad: false,
            condiciones: false,
            comunicaciones: false,
        };
        errorProcesar.value = null;
        resultadoCompra.value = null;
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        terminosAceptados,
        loadingProcesar,
        errorProcesar,
        resultadoCompra,
        procesarCompra,
        reset,
    };
}
